/**
 * Suggested allocation = min(PO outstanding, payment amount left after other rows).
 */

export function resolvePoOutstanding(po) {
  if (!po) return null;
  if (po.payment_outstanding != null && po.payment_outstanding !== "") {
    const outstanding = Number(po.payment_outstanding);
    return Number.isFinite(outstanding) ? Math.max(0, outstanding) : null;
  }
  const total = Number(po.total_amount);
  if (!Number.isFinite(total)) return null;
  const allocated = Number(po.total_payments_allocated ?? 0);
  const paid = Number.isFinite(allocated) ? allocated : 0;
  return Math.max(0, total - paid);
}

export function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

export function suggestAllocationAmount(paymentAmount, poOutstanding, otherAllocatedSum = 0) {
  const payment = Number(paymentAmount);
  const outstanding = Number(poOutstanding);
  if (!Number.isFinite(payment) || payment <= 0) return "";
  if (!Number.isFinite(outstanding) || outstanding <= 0) return "";

  const remainingPayment = Math.max(0, payment - Number(otherAllocatedSum || 0));
  const amount = Math.min(outstanding, remainingPayment);
  if (amount <= 0) return "";
  return roundMoney(amount);
}

export function resolveOutstandingFromMap(poOutstandingById, poId) {
  if (poId == null || !Number.isFinite(Number(poId))) return null;
  const key = Number(poId);
  const raw = poOutstandingById[key] ?? poOutstandingById[String(key)];
  return raw == null ? null : raw;
}

/**
 * @returns {Array<{ purchase_order_id: string|number, allocated_amount: string|number }>}
 */
export function buildAutoAllocatedRows({
  allocations,
  paymentAmount,
  poOutstandingById,
  manualRowIndices,
}) {
  const next = [];
  const reserved = allocations.reduce((sum, row, index) => {
    const poId = Number(row.purchase_order_id);
    const isManual = manualRowIndices.has(index);
    const hasUnknownOutstanding = poId && resolveOutstandingFromMap(poOutstandingById, poId) == null;
    return isManual || hasUnknownOutstanding
      ? sum + Number(row.allocated_amount || 0)
      : sum;
  }, 0);
  let allocated = reserved;

  for (let index = 0; index < allocations.length; index += 1) {
    const row = allocations[index];
    const poId = Number(row.purchase_order_id);
    if (!poId) {
      next.push(row);
      continue;
    }

    const outstanding = resolveOutstandingFromMap(poOutstandingById, poId);
    if (outstanding == null) {
      next.push(row);
      continue;
    }

    if (manualRowIndices.has(index)) {
      next.push(row);
      continue;
    }

    const suggested = suggestAllocationAmount(paymentAmount, outstanding, allocated);
    const nextAmount = suggested === "" ? "" : String(suggested);
    allocated += Number(nextAmount || 0);
    next.push(
      String(row.allocated_amount) === nextAmount ? row : { ...row, allocated_amount: nextAmount },
    );
  }

  return next;
}

export function allocationsChanged(before, after) {
  if (before.length !== after.length) return true;
  return after.some((row, index) => {
    const prev = before[index];
    return (
      String(row.purchase_order_id) !== String(prev.purchase_order_id) ||
      String(row.allocated_amount) !== String(prev.allocated_amount)
    );
  });
}
